/**
 * TrialBanner Component
 * 
 * Displays trial status banner at the top of the app for store owners.
 * Shows different urgency levels based on days remaining in trial.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.subscriptionStatus - User's subscription status object
 * @param {Function} props.onUpgrade - Callback when user clicks upgrade/view plans
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';

function TrialBanner({ subscriptionStatus, onUpgrade }) {
  if (!subscriptionStatus) return null;
  
  // Don't show for grandfathered users or active paid subscriptions
  if (subscriptionStatus.is_grandfathered || 
      (subscriptionStatus.subscription_status === 'active' && subscriptionStatus.subscription_type)) {
    return null;
  }
  
  // Show for free trial users
  if (subscriptionStatus.subscription_status === 'free_trial' && subscriptionStatus.trial_end_date) {
    const trialEnd = new Date(subscriptionStatus.trial_end_date);
    const now = new Date();
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 0) {
      return (
        <div className="bg-red-600 text-white shadow-lg" style={{ padding: '12px 24px' }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6" />
              <div>
                <p className="font-semibold">Your free trial has expired</p>
                <p className="text-sm opacity-90">Upgrade now to continue using CaterLog</p>
              </div>
            </div>
            <button
              onClick={onUpgrade}
              className="bg-white text-red-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      );
    }
    
    if (daysLeft <= 3) {
      return (
        <div className="bg-orange-500 text-white shadow-lg" style={{ padding: '12px 24px' }}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6" />
              <div>
                <p className="font-semibold">{daysLeft} {daysLeft === 1 ? 'day' : 'days'} left in your free trial</p>
                <p className="text-sm opacity-90">Upgrade to continue accessing all features</p>
              </div>
            </div>
            <button
              onClick={() => {
                console.log('🔵 View Plans clicked');
                onUpgrade();
              }}
              className="bg-white text-orange-500 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              View Plans
            </button>
          </div>
        </div>
      );
    }
    
    if (daysLeft <= 7) {
      return (
        <div className="bg-blue-600 text-white shadow-lg" style={{ padding: '8px 24px' }}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{daysLeft} days left in your free trial</span>
            <button
              onClick={onUpgrade}
              className="bg-white text-blue-600 font-medium hover:bg-gray-100 transition-colors text-sm"
              style={{ padding: '6px 16px', borderRadius: '9999px', minHeight: '32px' }}
            >
              View Plans
            </button>
          </div>
        </div>
      );
    }
    
    // Show subtle banner for 8-14 days remaining
    // Using same padding structure as nav-bawarchi (px-6 = 24px horizontal)
    return (
      <div className="bg-gray-100 border-b border-gray-300" style={{ padding: '8px 24px' }}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-700">{daysLeft} days left in your free trial</span>
          <button
            onClick={onUpgrade}
            className="bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors text-sm"
            style={{ padding: '6px 16px', borderRadius: '9999px', minHeight: '32px' }}
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }
  
  return null;
}

export default TrialBanner;
